import { AppDispatch } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
import { getUrlForImage } from "@CubeComponents/constants/globalFunctions";
import axiosWrapper from "../services/axiosWrapper";
import { CONSTANTS } from "@CubeComponents/constants/globalVariable";
import { formCategories } from "@Helpers";
import {
  setPortalUserPermissionAction,
  getPortalUserListAction,
  getPortalUserCountAction,
  setPortalDataAction,
  mergePortalDataAction,
  setPortalEntityListAction,
  setPortalCategoryListAction,
  getDsrDataAction,
} from "../reducers/portalSetting";
import type {
  GetPortalDataRequest,
  GetPortalDataResponse,
  GetPortalUsedSeatsRequest,
  GetPortalUsedSeatsResponse,
  ChangePortalRolePermissionRequest,
  ChangePortalRolePermissionResponse,
  GetPortalUsersRequest,
  GetPortalUsersResponse,
  AddUserToPortalRequest,
  AddUserToPortalResponse,
  ChangeUserPortalProfileRequest,
  ChangeUserPortalProfileResponse,
  GetPortalEntityListResponse,
  GetFormTemplateRequest,
  GetFormTemplateResponse,
  CustomModuleControllerResponse,
} from "@ServerTypes/portal/portal.schema";
import type {
  GetDsrDataRequest,
  GetDsrDataResponse,
  SaveDsrRequest,
  SaveDsrResponse,
} from "@ServerTypes/dsr/dsr.schema";
import type { UpdateGroupsDataRequest, UpdateGroupsDataResponse } from "@ServerTypes/groups/groups.schema";
import type {
  ProjectTemplateControllerRequest,
  ProjectTemplateControllerResponse,
} from "@ServerTypes/projectTemplates/projectTemplates.schema";
import type { GetResourceStatusCategoryResponse } from "@ServerTypes/resourceStatus/resourceStatus.schema";

// Custom module controller request can have different shapes based on action
interface CustomModuleControllerRequest {
  action?: "insert" | "update" | "delete";
  data?: {
    _id?: string;
    name?: string;
    description?: string;
    singularLabel?: string;
    pluralLabel?: string;
    icon?: string;
  };
  moduleId?: string;
  value?: {
    skip?: number;
    take?: number;
    sorted?: Array<{ name: string; direction: string }>;
    where?: unknown[];
    requiresCounts?: boolean;
  };
}

export function setSettingContent(data: any) {
  return {
    type: "SET_SETTING_CONTENT",
    data,
  };
}

export function setStatusData(data: any) {
  return {
    type: "SET_STATUS_LIST",
    data,
  };
}

const setPortalUserPermission = setPortalUserPermissionAction;
const getPortalUserList = getPortalUserListAction;
const getPortalUserCount = getPortalUserCountAction;
const setPortalData = setPortalDataAction;
const mergePortalData = mergePortalDataAction;
const setPortalEntityList = setPortalEntityListAction;
const setPortalCategoryList = setPortalCategoryListAction;
const getDsrData = getDsrDataAction;

export async function customModuleController(data: CustomModuleControllerRequest) {
  const responseData = await axiosWrapper.post<CustomModuleControllerResponse>(
    `${getBackendUrl()}/portal/customModuleController`,
    data,
  );
  return responseData;
}

export function getPortalData() {
  return async (dispatch: AppDispatch) => {
    const requestData: GetPortalDataRequest = {
      value: {
        where: [
          {
            isComplex: false,
            field: "frontendUrl",
            operator: "equal",
            value: window.location.host,
          },
        ],
      },
    };
    const response = await axiosWrapper.post<GetPortalDataResponse>(
      `${getBackendUrl()}/portal/getPortalData`,
      requestData,
    );
    const portalData = response.data.result?.[0] as Record<string, unknown> | undefined;

    dispatch(
      setPortalData({
        ...portalData,
        logoUrl: getUrlForImage({ id: portalData?._id as string | undefined }, CONSTANTS.PORTAL),
        signUpLogoUrl: getUrlForImage({ id: portalData?._id as string | undefined }, CONSTANTS.SIGNUP),
      }),
    );
  };
}

// NOTE: Backend returns result as number (count), but this code expects array with roleName/count.
// This is a pre-existing mismatch - keeping frontend logic intact with type assertion.
export function getPortalUsedSeats(data: GetPortalUsedSeatsRequest) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<GetPortalUsedSeatsResponse>(
      `${getBackendUrl()}/portal/getPortalUsedSeats`,
      data,
    );

    const mergedData = {
      usedProSeats: 0,
      usedLiteSeats: 0,
    };

    // Type assertion for pre-existing mismatch: backend returns number, frontend expects array
    const resultArray = (response.data.result ?? []) as Array<{ roleName: string; count: number }>;
    resultArray.forEach((item) => {
      if (item.roleName == "PORTAL_GUEST") {
        mergedData.usedLiteSeats = mergedData.usedLiteSeats + item.count;
      } else {
        mergedData.usedProSeats = mergedData.usedProSeats + item.count;
      }
    });

    dispatch(mergePortalData(mergedData));
  };
}

// updateProjectRolePermission
export function changePortalRolePermission(data: ChangePortalRolePermissionRequest) {
  return () => {
    axiosWrapper
      .post<ChangePortalRolePermissionResponse>(`${getBackendUrl()}/portal/changePortalRolePermission`, data)
      .then((response) => {
        if (response.data.status) {
          // console.log()
        }
      })
      .catch((err: unknown) => {
        console.log("Error in changePortalRolePermission ", err);
        Promise.reject(err);
      });
  };
}

export function getPortalUserDetails(data: GetPortalUsersRequest) {
  return (dispatch: AppDispatch) => {
    axiosWrapper.post<GetPortalUsersResponse>(`${getBackendUrl()}/portal/getPortalUsers`, data).then((response) => {
      if (response && response.status) {
        dispatch(getPortalUserList(response.data.result));
        dispatch(getPortalUserCount(response.data.count));
      }
    });
  };
}

// NOTE: Backend returns { status: true } only, no data field.
// Frontend expects response.data.data - pre-existing mismatch.
export function addUserToPortal(data: AddUserToPortalRequest) {
  return (dispatch: AppDispatch) => {
    axiosWrapper.post<AddUserToPortalResponse>(`${getBackendUrl()}/portal/addUserToPortal`, data).then((response) => {
      if (response.data.status) {
        // Type assertion for pre-existing mismatch: backend doesn't return data field
        dispatch(setPortalUserPermission((response.data as any).data));
      }
    });
  };
}

export async function changePortalRole(data: ChangeUserPortalProfileRequest): Promise<ChangeUserPortalProfileResponse> {
  const response = await axiosWrapper.post<ChangeUserPortalProfileResponse>(
    `${getBackendUrl()}/portal/changeUserPortalProfile`,
    data,
  );
  return response.data;
}

export function getAllPortalEntity() {
  return (dispatch: AppDispatch) => {
    axiosWrapper.get<GetPortalEntityListResponse>(`${getBackendUrl()}/portal/getPortalEntityList`).then((response) => {
      if (response.data.status) {
        dispatch(setPortalEntityList(response.data.data));
      }
    });
  };
}

export function getAllPortalCategories() {
  return (dispatch: AppDispatch) => {
    axiosWrapper
      .get<GetResourceStatusCategoryResponse>(`${getBackendUrl()}/resourceStatus/getResourceStatusCategory`)
      .then((response) => {
        if (response.data.status) {
          dispatch(setPortalCategoryList(response.data.data));
        }
      });
  };
}

export function fetchDSRData(data: GetDsrDataRequest) {
  return async (dispatch: AppDispatch) => {
    axiosWrapper.post<GetDsrDataResponse>(`${getBackendUrl()}/dsr/getDsrData`, data).then((res) => {
      if (res.data.status) {
        dispatch(getDsrData(res.data.result));
      }
    });
  };
}

export const saveAddEditCostDb = async (data: SaveDsrRequest): Promise<{ data: SaveDsrResponse }> => {
  const response = await axiosWrapper.post<SaveDsrResponse>(`${getBackendUrl()}/dsr/saveDsrData`, data);
  return response;
};

export const deleteCostDb = async (ids: string | string[]): Promise<{ data: GetDsrDataResponse }> => {
  const data: GetDsrDataRequest = {
    action: "remove",
    key: ids,
  };
  const response = await axiosWrapper.post<GetDsrDataResponse>(`${getBackendUrl()}/dsr/getDsrData`, data);
  return response;
};

export const updateGroupsData = async (data: UpdateGroupsDataRequest): Promise<{ data: UpdateGroupsDataResponse }> => {
  const response = await axiosWrapper.post<UpdateGroupsDataResponse>(
    `${getBackendUrl()}/groups/updateGroupsData`,
    data,
  );
  return response;
};

export const getProjectFormTemplate = async (): Promise<{ data: GetFormTemplateResponse }> => {
  const data: GetFormTemplateRequest = {
    resourceType: formCategories.projects,
  };
  const response = await axiosWrapper.post<GetFormTemplateResponse>(`${getBackendUrl()}/portal/getFormTemplate`, data);
  return response;
};

export const projectTemplateData = async (
  data: ProjectTemplateControllerRequest,
): Promise<{ data: ProjectTemplateControllerResponse }> => {
  const response = await axiosWrapper.post<ProjectTemplateControllerResponse>(
    `${getBackendUrl()}/projectTemplates/projectTemplateData`,
    data,
  );
  return response;
};

export const spaceDataInProjectTemplate = async (
  data: ProjectTemplateControllerRequest,
): Promise<{ data: ProjectTemplateControllerResponse }> => {
  const response = await axiosWrapper.post<ProjectTemplateControllerResponse>(
    `${getBackendUrl()}/projectTemplates/spacesDataInProjectTemplate`,
    data,
  );
  return response;
};
